/* ==========================================================================
   B97 Orbit — theme.js
   Sin dependencias. Componentes como Custom Elements: solo se activan si
   el elemento existe en la página. Carrito real vía Ajax API de Shopify y
   Section Rendering API (nada simulado en el navegador).
   ========================================================================== */
(() => {
  'use strict';

  const B97 = (window.B97 = window.B97 || {});
  const config = window.B97Config || { strings: {} };
  const routes = window.routes || {};
  const strings = config.strings || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ------------------------------------------------------------------------
     Utilidades
     ------------------------------------------------------------------------ */
  B97.debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  B97.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  B97.announce = (message) => {
    const live = document.getElementById('B97Live');
    if (!live || !message) return;
    live.textContent = '';
    window.setTimeout(() => { live.textContent = message; }, 60);
  };

  B97.toast = (message, type = 'error') => {
    if (!message) return;
    let toast = document.querySelector('.b97-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'b97-toast';
      toast.setAttribute('role', 'alert');
      document.body.appendChild(toast);
    }
    toast.dataset.type = type;
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._t);
    toast._t = setTimeout(() => toast.classList.remove('is-visible'), 4500);
  };

  /* Formato de dinero de Shopify ({{amount}}, {{amount_with_comma_separator}}…) */
  B97.formatMoney = (cents, format = config.moneyFormat || '{{amount}} €') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (number, precision, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    };
    const match = format.match(placeholder);
    if (!match) return fmt(cents, 2);
    let value;
    switch (match[1]) {
      case 'amount': value = fmt(cents, 2); break;
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' '); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = fmt(cents, 2, ' ', '.'); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(placeholder, value);
  };

  const fetchJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {})
      }
    });
    let data = null;
    try { data = await response.json(); } catch (e) { data = null; }
    if (!response.ok || (data && data.status && Number(data.status) >= 400)) {
      const message = (data && (data.description || data.message)) || strings.cartError;
      const error = new Error(typeof message === 'string' ? message : strings.cartError);
      error.data = data;
      throw error;
    }
    return data;
  };
  B97.fetchJSON = fetchJSON;

  /* ------------------------------------------------------------------------
     Cajones sobre <dialog>: foco atrapado, Esc y fondo nativos
     ------------------------------------------------------------------------ */
  class B97Drawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector(':scope > dialog');
      if (!this.dialog || this._ready) return;
      this._ready = true;
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        if (!inside) this.close();
      });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
        }
      });
    }

    get isOpen() {
      return this.dialog && this.dialog.open;
    }

    open(trigger) {
      if (!this.dialog || this.isOpen) return;
      B97.closeDrawers(this);
      this.trigger = trigger || document.activeElement;
      this.dialog.showModal();
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      if (trigger && trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'true');
      const focusTarget = this.dialog.querySelector('[data-autofocus], input[type="search"]');
      if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 60);
      document.dispatchEvent(new CustomEvent('b97:drawer-open', { detail: { id: this.id } }));
    }

    close() {
      if (!this.isOpen || this._closing) return;
      this._closing = true;
      this.classList.remove('is-open');
      const finish = () => {
        clearTimeout(timer);
        this.dialog.removeEventListener('transitionend', onEnd);
        this.dialog.close();
        this._closing = false;
        if (this.trigger && this.trigger.hasAttribute && this.trigger.hasAttribute('aria-expanded')) this.trigger.setAttribute('aria-expanded', 'false');
        if (this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
        document.dispatchEvent(new CustomEvent('b97:drawer-close', { detail: { id: this.id } }));
      };
      const onEnd = (event) => { if (event.target === this.dialog) finish(); };
      const timer = setTimeout(finish, reduceMotion.matches ? 0 : 560);
      this.dialog.addEventListener('transitionend', onEnd);
    }
  }
  if (!customElements.get('b97-drawer')) customElements.define('b97-drawer', B97Drawer);

  B97.closeDrawers = (except) => {
    document.querySelectorAll('b97-drawer, cart-drawer').forEach((drawer) => {
      if (drawer !== except && drawer.isOpen) {
        drawer.classList.remove('is-open');
        drawer.dialog.close();
      }
    });
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(opener);
  });

  /* ------------------------------------------------------------------------
     Cabecera
     ------------------------------------------------------------------------ */
  class HeaderBar extends HTMLElement {
    connectedCallback() {
      this.overlay = this.dataset.overlay === 'true';
      this.sticky = this.classList.contains('hdr--sticky');
      this.dropdowns = Array.from(this.querySelectorAll('[data-dropdown]'));
      this.lastY = window.scrollY;

      const setHeight = () => document.documentElement.style.setProperty('--hdr-h', `${this.querySelector('.hdr__bar').offsetHeight}px`);
      setHeight();
      if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(this.querySelector('.hdr__bar'));

      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.dropdowns.forEach((details) => {
        let leaveTimer;
        details.addEventListener('toggle', () => {
          if (details.open) this.dropdowns.forEach((other) => { if (other !== details) other.open = false; });
          this.updateSolid();
        });
        details.addEventListener('mouseenter', () => {
          if (!canHover.matches) return;
          clearTimeout(leaveTimer);
          details.open = true;
        });
        details.addEventListener('mouseleave', () => {
          if (!canHover.matches) return;
          leaveTimer = setTimeout(() => { details.open = false; }, 160);
        });
        details.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && details.open) {
            details.open = false;
            details.querySelector('summary').focus();
          }
        });
        details.addEventListener('focusout', (event) => {
          if (!details.contains(event.relatedTarget)) details.open = false;
        });
      });
      document.addEventListener('click', (event) => {
        this.dropdowns.forEach((details) => { if (details.open && !details.contains(event.target)) details.open = false; });
      });
      document.addEventListener('b97:drawer-open', () => this.updateSolid());
      document.addEventListener('b97:drawer-close', () => this.updateSolid());
    }

    onScroll() {
      const y = window.scrollY;
      this.updateSolid();
      if (this.sticky) {
        const goingDown = y > this.lastY;
        const anyOpen = this.dropdowns.some((d) => d.open);
        this.classList.toggle('hdr--hidden', goingDown && y > 480 && !anyOpen);
      }
      this.lastY = y;
    }

    updateSolid() {
      if (!this.overlay) return;
      const solid = window.scrollY > 8 || this.dropdowns.some((d) => d.open);
      this.classList.toggle('is-solid', solid);
    }
  }
  if (!customElements.get('header-bar')) customElements.define('header-bar', HeaderBar);

  /* ------------------------------------------------------------------------
     Carrito (Ajax API + Section Rendering API)
     ------------------------------------------------------------------------ */
  const cartPageSection = () => document.querySelector('cart-page');

  B97.cart = {
    sectionIds() {
      const ids = ['cart-drawer', 'cart-count'];
      const page = cartPageSection();
      if (page && page.dataset.section) ids.push(page.dataset.section);
      return ids;
    },

    async add(input) {
      let body;
      if (input instanceof FormData) {
        body = input;
        body.append('sections', this.sectionIds().join(','));
        body.append('sections_url', window.location.pathname);
      } else {
        body = JSON.stringify({ ...input, sections: this.sectionIds(), sections_url: window.location.pathname });
      }
      const data = await fetchJSON(`${routes.cartAdd}.js`, { method: 'POST', body });
      if (data && data.sections) this.render(data.sections);
      B97.announce(strings.itemAdded);
      document.dispatchEvent(new CustomEvent('b97:cart-added', { detail: data }));
      return data;
    },

    async change(key, quantity) {
      const data = await fetchJSON(`${routes.cartChange}.js`, {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity, sections: this.sectionIds(), sections_url: window.location.pathname })
      });
      if (data && data.sections) this.render(data.sections);
      B97.announce(strings.cartUpdated);
      return data;
    },

    async updateNote(note) {
      return fetchJSON(`${routes.cartUpdate}.js`, { method: 'POST', body: JSON.stringify({ note }) });
    },

    render(sections) {
      if (sections['cart-drawer']) {
        const doc = B97.parseHTML(sections['cart-drawer']);
        const fresh = doc.querySelector('[data-cart-drawer-inner]');
        const current = document.querySelector('#CartDrawer [data-cart-drawer-inner]');
        if (fresh && current) current.replaceWith(fresh);
      }
      if (sections['cart-count']) {
        const doc = B97.parseHTML(sections['cart-count']);
        const value = doc.querySelector('[data-cart-count-value]');
        if (value) this.setCount(parseInt(value.textContent, 10) || 0);
      }
      const page = cartPageSection();
      if (page && sections[page.dataset.section]) {
        const doc = B97.parseHTML(sections[page.dataset.section]);
        const fresh = doc.querySelector('[data-cart-page-inner]');
        const current = page.querySelector('[data-cart-page-inner]');
        if (fresh && current) current.replaceWith(fresh);
      }
    },

    setCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        const changed = el.textContent.trim() !== String(count);
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
        if (changed && !reduceMotion.matches) {
          el.classList.remove('is-bump');
          void el.offsetWidth;
          el.classList.add('is-bump');
        }
      });
      document.querySelectorAll('[data-cart-link]').forEach((link) => {
        if (link.dataset.labelTemplate) link.setAttribute('aria-label', link.dataset.labelTemplate.replace('[count]', count));
      });
    },

    openDrawer(trigger) {
      const drawer = document.getElementById('CartDrawer');
      if (drawer && config.cartType === 'drawer') {
        drawer.open(trigger);
        return true;
      }
      return false;
    },

    afterAdd(trigger) {
      if (config.cartType === 'page') {
        window.location.href = routes.cart;
        return;
      }
      if (!this.openDrawer(trigger) && !cartPageSection()) window.location.href = routes.cart;
    }
  };

  class CartDrawer extends B97Drawer {}
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  /* Enlace de la bolsa en la cabecera */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cart-link]');
    if (!link || event.metaKey || event.ctrlKey) return;
    if (config.cartType !== 'drawer' || cartPageSection()) return;
    if (B97.cart.openDrawer(link)) event.preventDefault();
  });

  /* Cantidades y eliminar líneas (cajón y página) */
  const lineTimers = new WeakMap();
  const updateLine = async (line, quantity) => {
    const key = line.dataset.lineKey;
    const errorEl = line.querySelector('[data-line-error]');
    const input = line.querySelector('[data-line-qty]');
    line.classList.add('is-updating');
    if (errorEl) errorEl.hidden = true;
    try {
      await B97.cart.change(key, quantity);
    } catch (error) {
      line.classList.remove('is-updating');
      if (input && input.dataset.last) input.value = input.dataset.last;
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      } else {
        B97.toast(error.message);
      }
    }
  };

  document.addEventListener('click', (event) => {
    const stepBtn = event.target.closest('[data-qty-step]');
    if (stepBtn) {
      const wrap = stepBtn.closest('.qty');
      const input = wrap && wrap.querySelector('input[type="number"]');
      if (!input) return;
      const step = Number(input.step) || 1;
      const min = Number(input.min) || 0;
      const max = input.max ? Number(input.max) : Infinity;
      const current = Number(input.value) || 0;
      if (!input.dataset.last) input.dataset.last = current;
      let next = current + Number(stepBtn.dataset.qtyStep) * step;
      next = Math.min(max, Math.max(min, next));
      if (next === current) return;
      input.value = next;
      const line = stepBtn.closest('.cline');
      if (line) {
        clearTimeout(lineTimers.get(line));
        lineTimers.set(line, setTimeout(() => updateLine(line, Number(input.value)), 380));
      } else {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const remove = event.target.closest('[data-line-remove]');
    if (remove) {
      const line = remove.closest('.cline');
      if (!line) return;
      event.preventDefault();
      updateLine(line, 0);
    }
  });

  document.addEventListener('focusin', (event) => {
    if (event.target.matches('[data-line-qty]')) event.target.dataset.last = event.target.value;
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-line-qty]');
    if (!input) return;
    const line = input.closest('.cline');
    if (!line) return;
    clearTimeout(lineTimers.get(line));
    const value = Math.max(0, parseInt(input.value, 10) || 0);
    updateLine(line, value);
  });

  const saveNote = B97.debounce((value) => {
    B97.cart.updateNote(value).catch((error) => B97.toast(error.message));
  }, 600);
  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-cart-note]')) saveNote(event.target.value);
  });

  /* Página del carrito */
  class CartPage extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      if (form) form.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('[data-line-qty]')) {
          event.preventDefault();
          event.target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }
  if (!customElements.get('cart-page')) customElements.define('cart-page', CartPage);

  /* ------------------------------------------------------------------------
     Añadir rápido desde la tarjeta
     ------------------------------------------------------------------------ */
  document.addEventListener('click', async (event) => {
    const toggle = event.target.closest('[data-quick-toggle]');
    if (toggle) {
      event.preventDefault();
      const wrap = toggle.closest('[data-quick-sizes]');
      const open = !wrap.classList.contains('is-open');
      document.querySelectorAll('[data-quick-sizes].is-open').forEach((el) => {
        el.classList.remove('is-open');
        el.querySelector('[data-quick-toggle]').setAttribute('aria-expanded', 'false');
      });
      wrap.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      return;
    }

    const btn = event.target.closest('[data-quick-add]');
    if (!btn) {
      if (!event.target.closest('[data-quick-sizes]')) {
        document.querySelectorAll('[data-quick-sizes].is-open').forEach((el) => {
          el.classList.remove('is-open');
          el.querySelector('[data-quick-toggle]').setAttribute('aria-expanded', 'false');
        });
      }
      return;
    }
    event.preventDefault();
    if (btn.disabled || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    try {
      await B97.cart.add({ items: [{ id: Number(btn.dataset.quickAdd), quantity: 1 }] });
      btn.classList.add('is-added');
      setTimeout(() => btn.classList.remove('is-added'), 1600);
      const wrap = btn.closest('[data-quick-sizes]');
      if (wrap) wrap.classList.remove('is-open');
      B97.cart.afterAdd(btn);
    } catch (error) {
      B97.toast(error.message);
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  });

  /* ------------------------------------------------------------------------
     Búsqueda predictiva
     ------------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-results]');
      this.status = this.querySelector('[data-status]');
      if (!this.input || this.dataset.enabled !== 'true' || !routes.predictiveSearch) return;
      this.cache = new Map();
      this.input.addEventListener('input', B97.debounce(() => this.search(), 220));
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      if (this.cache.has(q)) {
        this.show(this.cache.get(q));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,query,page',
        'resources[limit]': '8',
        'resources[limit_scope]': 'each'
      });
      this.classList.add('is-loading');
      try {
        const response = await fetch(`${routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        const html = await response.text();
        const doc = B97.parseHTML(html);
        const section = doc.querySelector('#shopify-section-predictive-search') || doc.body;
        const markup = section.innerHTML;
        this.cache.set(q, markup);
        this.show(markup);
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      } finally {
        this.classList.remove('is-loading');
      }
    }

    show(markup) {
      this.results.innerHTML = markup;
      const inner = this.results.querySelector('[data-predictive-count]');
      if (this.status && inner) this.status.textContent = `${inner.dataset.predictiveCount}`;
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------------
     Barra de anuncios
     ------------------------------------------------------------------------ */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messages = Array.from(this.querySelectorAll('.announce__msg'));
      if (this.messages.length < 2) return;
      this.index = 0;
      this.paused = false;
      this.pauseBtn = this.querySelector('[data-pause]');
      const interval = Number(this.dataset.interval) || 5000;
      this.timer = setInterval(() => { if (!this.paused && !this.hovering) this.next(); }, interval);
      this.addEventListener('mouseenter', () => { this.hovering = true; });
      this.addEventListener('mouseleave', () => { this.hovering = false; });
      this.addEventListener('focusin', () => { this.hovering = true; });
      this.addEventListener('focusout', () => { this.hovering = false; });
      if (this.pauseBtn) this.pauseBtn.addEventListener('click', () => {
        this.paused = !this.paused;
        this.pauseBtn.setAttribute('aria-pressed', String(this.paused));
      });
    }
    next() {
      this.messages[this.index].hidden = true;
      this.messages[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.messages.length;
      this.messages[this.index].hidden = false;
      this.messages[this.index].classList.add('is-active');
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  /* ------------------------------------------------------------------------
     Ciudades de la marca con su hora local (Intl, sin servicios externos)
     ------------------------------------------------------------------------ */
  class CityClock extends HTMLElement {
    connectedCallback() {
      this.clocks = Array.from(this.querySelectorAll('[data-clock]'));
      if (!this.clocks.length || !window.Intl) return;
      const lang = document.documentElement.lang || 'es';
      this.formats = this.clocks.map((el) => {
        const zone = el.dataset.clock === 'a' ? this.dataset.tzA : this.dataset.tzB;
        if (!zone) return null;
        try {
          return new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: zone });
        } catch (error) {
          return null; // zona horaria no válida: se muestra solo el nombre de la ciudad
        }
      });
      this.tick();
      this.timeout = setTimeout(() => {
        this.tick();
        this.interval = setInterval(() => this.tick(), 60000);
      }, 60000 - (Date.now() % 60000) + 50);
    }
    disconnectedCallback() {
      clearTimeout(this.timeout);
      clearInterval(this.interval);
    }
    tick() {
      const now = new Date();
      this.clocks.forEach((el, i) => {
        const format = this.formats[i];
        if (!format) { el.hidden = true; return; }
        const text = format.format(now);
        el.textContent = text;
        el.setAttribute('datetime', text);
        el.hidden = false;
      });
    }
  }
  if (!customElements.get('city-clock')) customElements.define('city-clock', CityClock);

  /* ------------------------------------------------------------------------
     Explorador de look (puntos sobre la imagen)
     ------------------------------------------------------------------------ */
  class LookExplorer extends HTMLElement {
    connectedCallback() {
      this.spots = Array.from(this.querySelectorAll('[data-spot]'));
      this.rows = Array.from(this.querySelectorAll('[data-spot-row]'));
      this.spots.forEach((spot) => {
        const dot = spot.querySelector('.look__dot');
        dot.addEventListener('click', (event) => {
          event.stopPropagation();
          this.toggle(spot);
        });
        dot.addEventListener('mouseenter', () => this.hot(spot.dataset.spot, true));
        dot.addEventListener('mouseleave', () => this.hot(spot.dataset.spot, false));
        dot.addEventListener('focus', () => this.hot(spot.dataset.spot, true));
        dot.addEventListener('blur', () => this.hot(spot.dataset.spot, false));
      });
      this.rows.forEach((row) => {
        row.addEventListener('mouseenter', () => this.hot(row.dataset.spotRow, true));
        row.addEventListener('mouseleave', () => this.hot(row.dataset.spotRow, false));
        row.addEventListener('focusin', () => this.hot(row.dataset.spotRow, true));
        row.addEventListener('focusout', () => this.hot(row.dataset.spotRow, false));
      });
      document.addEventListener('click', (event) => {
        if (!event.target.closest('.look__pop')) this.closeAll();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          const open = this.spots.find((s) => s.classList.contains('is-open'));
          this.closeAll();
          if (open) open.querySelector('.look__dot').focus();
        }
      });
    }
    toggle(spot) {
      const willOpen = !spot.classList.contains('is-open');
      this.closeAll();
      if (!willOpen) return;
      spot.classList.add('is-open');
      spot.querySelector('.look__dot').setAttribute('aria-expanded', 'true');
      spot.querySelector('.look__pop').hidden = false;
    }
    closeAll() {
      this.spots.forEach((spot) => {
        spot.classList.remove('is-open');
        spot.querySelector('.look__dot').setAttribute('aria-expanded', 'false');
        spot.querySelector('.look__pop').hidden = true;
      });
    }
    hot(id, on) {
      this.spots.forEach((s) => s.classList.toggle('is-hot', on && s.dataset.spot === id));
      this.rows.forEach((r) => r.classList.toggle('is-hot', on && r.dataset.spotRow === id));
    }
  }
  if (!customElements.get('look-explorer')) customElements.define('look-explorer', LookExplorer);

  /* ------------------------------------------------------------------------
     Recomendaciones de producto (Shopify Product Recommendations)
     ------------------------------------------------------------------------ */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.querySelector('.pgrid')) return;
      const load = async () => {
        try {
          const response = await fetch(this.dataset.url);
          const html = await response.text();
          const fresh = B97.parseHTML(html).querySelector('product-recommendations');
          if (fresh && fresh.querySelector('.pgrid')) this.innerHTML = fresh.innerHTML;
          else this.remove();
        } catch (e) {
          this.remove();
        }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            load();
          }
        }, { rootMargin: '0px 0px 500px 0px' });
        io.observe(this);
      } else {
        load();
      }
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------------------
     Órbitas que se dibujan (hero y entrada)
     ------------------------------------------------------------------------ */
  B97.drawOrbit = (svg) => {
    const finish = () => {
      svg.classList.remove('is-drawing');
      svg.classList.add('is-drawn');
    };
    const ellipse = svg.querySelector('ellipse');
    if (reduceMotion.matches || !ellipse || !ellipse.getScreenCTM || !window.DOMPoint) return finish();
    const m = ellipse.getScreenCTM();
    if (!m) return finish();
    const cx = Number(ellipse.getAttribute('cx'));
    const cy = Number(ellipse.getAttribute('cy'));
    const rx = Number(ellipse.getAttribute('rx'));
    const ry = Number(ellipse.getAttribute('ry'));
    let length = 0;
    let prev = null;
    for (let i = 0; i <= 120; i++) {
      const t = (i / 120) * Math.PI * 2;
      const pt = new DOMPoint(cx + rx * Math.cos(t), cy + ry * Math.sin(t)).matrixTransform(m);
      if (prev) length += Math.hypot(pt.x - prev.x, pt.y - prev.y);
      prev = pt;
    }
    if (!length) return finish();
    svg.style.setProperty('--len', `${Math.ceil(length) + 4}`);
    svg.classList.add('is-drawing');
    svg.addEventListener('animationend', (event) => {
      if (event.animationName === 'orbitDraw') finish();
    });
  };
  document.querySelectorAll('.hero__orbit, .b97-intro__orbit').forEach((svg) => B97.drawOrbit(svg));

  /* ------------------------------------------------------------------------
     Animación de entrada
     ------------------------------------------------------------------------ */
  const intro = document.querySelector('[data-intro]');
  if (intro && document.documentElement.classList.contains('intro-play')) {
    try { sessionStorage.setItem('b97-intro', '1'); } catch (e) { /* almacenamiento no disponible */ }
    const skip = () => intro.classList.add('is-skipping');
    intro.addEventListener('click', skip);
    window.addEventListener('keydown', skip, { once: true });
    window.addEventListener('wheel', skip, { once: true, passive: true });
    window.addEventListener('touchmove', skip, { once: true, passive: true });
    intro.addEventListener('animationend', (event) => {
      if (event.animationName === 'introOut') intro.remove();
    });
  } else if (intro) {
    intro.remove();
  }

  /* ------------------------------------------------------------------------
     Apariciones al desplazarse (solo los elementos que lo usan en CSS)
     ------------------------------------------------------------------------ */
  const revealTargets = document.querySelectorAll('[data-reveal]');
  if (document.body.classList.contains('reveal-on') && revealTargets.length) {
    if (designMode || !('IntersectionObserver' in window)) {
      revealTargets.forEach((el) => el.classList.add('is-in'));
    } else {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.18, rootMargin: '0px 0px -6% 0px' });
      revealTargets.forEach((el) => io.observe(el));
    }
  } else {
    revealTargets.forEach((el) => el.classList.add('is-in'));
  }

  /* ------------------------------------------------------------------------
     Transición de la foto de la tarjeta a la ficha (View Transitions)
     ------------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-card-link]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
    document.querySelectorAll('.card__img--main').forEach((img) => { img.style.viewTransitionName = ''; });
    const card = link.closest('[data-product-card]');
    const img = card && card.querySelector('.card__img--main');
    if (img) img.style.viewTransitionName = 'b97-media';
  });
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.card__img--main').forEach((img) => { img.style.viewTransitionName = ''; });
  });

  /* ------------------------------------------------------------------------
     Selector de país / idioma y compartir
     ------------------------------------------------------------------------ */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('select[data-auto-submit]');
    if (select && select.form) select.form.submit();
  });

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-share]');
    if (!btn) return;
    const url = btn.dataset.shareUrl || window.location.href;
    const status = btn.parentElement.querySelector('[data-share-status]');
    if (navigator.share) {
      try { await navigator.share({ title: btn.dataset.shareTitle || document.title, url }); } catch (e) { /* cancelado */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      if (status) {
        status.textContent = strings.linkCopied;
        setTimeout(() => { status.textContent = ''; }, 2500);
      }
    } catch (e) {
      window.prompt('', url);
    }
  });

  /* Mensajes de formularios tras recargar (newsletter, contacto) */
  const autofocusMsg = document.querySelector('[data-autofocus].form-msg, [data-autofocus].nl-form__msg');
  if (autofocusMsg) {
    autofocusMsg.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    autofocusMsg.focus({ preventScroll: true });
  }
})();
